import React, { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "react-toastify";
import type { QueueEntryListItem } from "../../services/queue.service";
import { QueueAPI } from "../../services/queue.service";
import { QueueStateBadge } from "./QueueStateBadge";
import { useRole } from "../../hooks/useRole";

const PRIORITY_LABEL: Record<string, string> = {
  senior_citizen: "Senior Citizen",
  emergency: "Emergency",
  staff_family: "Staff Family",
};

const fmt = (iso?: string | null) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

interface Props {
  entry: QueueEntryListItem;
  queueNum: number;
  onRefresh: () => void;
}

export const QueueRow: React.FC<Props> = ({ entry, queueNum, onRefresh }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const { isDoctor } = useRole();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.queue_entry_id,
    disabled: entry.state !== "waiting", // only the manually-orderable line is draggable
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    try {
      await fn();
      toast.success(label);
      onRefresh();
    } catch (e: any) {
      toast.error(e.message || `Failed: ${label}`);
    } finally {
      setBusy(null);
    }
  };

  const isActive = ["waiting", "in_progress", "deferred"].includes(entry.state);
  // Mirrors the backend's role gates exactly (queue/router.py): accept/defer/
  // reject are Doctor-only (403 for anyone else), mark_left is Admin/
  // Receptionist-only (403 for a Doctor) - so these aren't just UX polish,
  // they keep the buttons shown in sync with what the API would actually allow.
  const canAccept = isDoctor && ["waiting", "deferred"].includes(entry.state);
  const canDefer = isDoctor && entry.state === "waiting";
  const canReject = isDoctor && ["waiting", "deferred"].includes(entry.state);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-2xl border border-slate-100 border-l-4 border-l-purple-300 shadow-sm px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 hover:shadow-md transition-shadow"
    >
      {/* Drag handle - only active for 'waiting' entries */}
      <button
        {...attributes}
        {...listeners}
        disabled={entry.state !== "waiting"}
        className="w-6 text-slate-300 hover:text-slate-500 disabled:opacity-20 disabled:cursor-not-allowed cursor-grab active:cursor-grabbing flex-shrink-0"
        aria-label="Drag to reorder"
      >
        ⠿
      </button>

      <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
        <span className="text-xs font-bold text-purple-500">{queueNum}</span>
      </div>

      <div className="w-20 flex-shrink-0 text-center">
        <div className="text-sm font-bold text-slate-800">
          {entry.eta_minutes != null ? `~${entry.eta_minutes}m` : fmt(entry.suggested_time)}
        </div>
        <div className="text-xs text-slate-400">ETA</div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-800 truncate">{entry.patient_name}</span>
          <QueueStateBadge state={entry.state} size="sm" />
          {entry.priority_tag !== "none" && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">
              {PRIORITY_LABEL[entry.priority_tag]}
            </span>
          )}
        </div>
        <div className="text-xs text-slate-400 mt-0.5 truncate">{entry.doctor_name}</div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
        {canAccept && (
          <button
            onClick={() => act("Accepted", () => QueueAPI.accept(entry.queue_entry_id))}
            disabled={!!busy}
            className="inline-flex items-center justify-center px-3 min-h-[44px] rounded-lg text-xs font-medium bg-cyan-500 text-white hover:bg-cyan-600 disabled:opacity-40 transition"
          >
            {busy === "Accepted" ? "…" : "Accept"}
          </button>
        )}
        {canDefer && (
          <button
            onClick={() => act("Deferred", () => QueueAPI.defer(entry.queue_entry_id))}
            disabled={!!busy}
            className="inline-flex items-center justify-center px-3 min-h-[44px] rounded-lg text-xs font-medium bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40 transition"
          >
            {busy === "Deferred" ? "…" : "Defer"}
          </button>
        )}
        {canReject && (
          <button
            onClick={() => act("Rejected", () => QueueAPI.reject(entry.queue_entry_id))}
            disabled={!!busy}
            className="inline-flex items-center justify-center px-3 min-h-[44px] rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-40 transition"
          >
            {busy === "Rejected" ? "…" : "Reject"}
          </button>
        )}
        {isActive && (
          <>
            <button
              onClick={() => act("Marked seen", () => QueueAPI.markSeen(entry.queue_entry_id))}
              disabled={!!busy}
              className="inline-flex items-center justify-center px-3 min-h-[44px] rounded-lg text-xs font-medium bg-green-500 text-white hover:bg-green-600 disabled:opacity-40 transition"
            >
              {busy === "Marked seen" ? "…" : "Mark Seen"}
            </button>
            {/* Mark Left is meaningless for a doctor (backend also 403s it for
                that role) - front desk is the one who witnesses a walk-out. */}
            {!isDoctor && (
              <button
                onClick={() => act("Marked left", () => QueueAPI.markLeft(entry.queue_entry_id))}
                disabled={!!busy}
                className="inline-flex items-center justify-center px-3 min-h-[44px] rounded-lg text-xs font-medium bg-slate-100 text-slate-500 hover:bg-slate-200 disabled:opacity-40 transition"
              >
                {busy === "Marked left" ? "…" : "Mark Left"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};
